import React, { useEffect, useContext, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppContext } from "../../contexts/AppContext";
import GeneralButton from "../../components/GeneralButton/GeneralButton";
import "../../styles/globals.css";
import "./CertificationNumberInputPage.css";
import { Form, Input, Row, Col } from "antd";
import { ExclamationCircleOutlined, WarningFilled } from "@ant-design/icons";
import {
  registAccount,
  checkCertificationNumber,
  updateEmail,
  sendCertificationNumberMailWhenPasswordReset,
  sendCertificationNumberMailWhenAccountRegist,
  sendCertificationNumberMailWhenEmailUpdate,
  checkCertificationNumberBeforeLogin,
  sendCertificationNumberMailWhenPasswordResetBeforeLogin,
} from "../../services/service";
import { showToast, encodeToBase64 } from "../../utils/commonUtils";
import { validateCertificationNumber } from "../../utils/validationUtils";
import { Mail_TYPES } from "../../config/setting";

/**
 * 認証番号入力画面用のコンポーネント
 * @param {} なし
 * @return {} なし
 */
function CertificationNumberInputPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    globalEmailAddress,
    globalLoginUserInfo,
    globalReservationInput,
    globalAccessToken,
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [errors, setErrors] = useState({
    certification: false,
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [certificationNumberCheck, setCertificationNumberCheck] =
    useState(false);

  const certificationInput = useRef(null);

  useEffect(() => {
    certificationInput.current.input.focus();
  }, [errors?.certification]);

  // 認証番号送信処理
  const handleVerificationClick = async () => {
    await form.validateFields();

    const values = form.getFieldsValue();
    const { certification } = values;
    let data = "";

    const inputData = {
      kanjiLast: globalReservationInput?.kanjiLast,
      kanjiFirst: globalReservationInput?.kanjiFirst,
      kanaLast: globalReservationInput?.kanaLast,
      kanaFirst: globalReservationInput?.kanaFirst,
      birthday: globalReservationInput?.birthday,
      sex: globalReservationInput?.sex,
      email: globalReservationInput?.email,
      tel: globalReservationInput?.tel,
      zipCode: globalReservationInput?.zipCode,
      prefecture: globalReservationInput?.prefecture,
      city: globalReservationInput?.city,
      street: globalReservationInput?.street,
      building: globalReservationInput?.building,
    };
    try {
      // アカウント登録用
      if (location.pathname?.includes("account-regist")) {
        data = await registAccount(encodeToBase64(certification), inputData);
      }

      // マイページ-パスワード変更用
      if (location.pathname?.includes("mypage/password-setting")) {
        data = await checkCertificationNumber(
          globalAccessToken,
          encodeToBase64(certification),
          globalEmailAddress
        );
      }
      // パスワードをお忘れの方はこちらから用（ログイン前）
      if (location.pathname?.includes("reset-password")) {
        data = await checkCertificationNumberBeforeLogin(
          encodeToBase64(certification),
          globalEmailAddress
        );
      }
      // マイページ-メールアドレス変更用
      if (location.pathname?.includes("mypage/mail-setting")) {
        data = await updateEmail(
          globalAccessToken,
          encodeToBase64(certification),
          globalLoginUserInfo?.userInfo?.email,
          globalEmailAddress,
          globalLoginUserInfo?.userInfo.kanjiLast,
          globalLoginUserInfo?.userInfo.kanjiFirst
        );
      }

      if (data.result.status === "OK") {
        // アカウント登録用
        if (location.pathname?.includes("account-regist")) {
          // アカウント登録完了画面に遷移
          navigate("/account-regist-done");
        }
        // パスワードをお忘れの方はこちらから用
        if (location.pathname?.includes("reset-password")) {
          // パスワード再設定画面に遷移
          navigate("/regist-new-password");
        }
        // マイページ-パスワード変更用
        if (location.pathname?.includes("mypage/password-setting")) {
          // パスワード再設定画面(マイページ)に遷移
          navigate("/mypage/password-setting/regist-new-password");
        }
        // マイページ-メールアドレス変更用
        if (location.pathname?.includes("mypage/mail-setting")) {
          showToast("メールアドレスを変更しました", "success");

          // マイページ画面に遷移
          navigate("/mypage/menu");
        }
      } else if (data.result.status === "NG" && data.result.error_no == 1100) {
        // エラーメッセージセット
        setErrors({
          ...errors,
          certification: "認証番号を再度入力してください",
        });
        setErrorMessage(
          "認証番号が間違っています。再度入力をお願いいたします。"
        );
      } else if (data.result.status === "NG" && data.result.error_no == 1102) {
        // エラーメッセージセット
        setErrorMessage(
          "認証番号の有効期限が切れています。設定メールの再送信をお願いいたします。"
        );
      } else {
        showToast(data?.result?.error_message, "error");
      }
    } catch (error) {
      // アカウント登録用
      if (location.pathname?.includes("account-regist")) {
        showToast("アカウント登録に失敗しました", "error");
      }
      // パスワードをお忘れの方はこちらから用
      // マイページ-パスワード変更用
      if (
        location.pathname?.includes("reset-password") ||
        location.pathname?.includes("mypage/password-setting")
      ) {
        showToast("パスワード再設定に失敗しました", "error");
      }
      // マイページ-メールアドレス変更用
      if (location.pathname?.includes("mypage/mail-setting")) {
        showToast("メールアドレス変更に失敗しました", "error");
      }
      console.error("Error registAccount:", error);
    }
  };

  /**
   * 値変更時の処理
   * @param {string} fieldName - フィールド名
   */
  const onValueChange = async (fieldName) => {
    try {
      await form.validateFields([fieldName]);
      setErrors({ ...errors, [fieldName]: false });
      setErrorMessage(null);
      setCertificationNumberCheck(true);
    } catch (error) {
      setCertificationNumberCheck(false);
      setErrors({ ...errors, [fieldName]: true });
    }
  };

  ///メール再送信
  const handleResendMail = async () => {
    try {
      let data = "";
      // アカウント登録用
      if (location.pathname?.includes("account-regist")) {
        data = await sendCertificationNumberMailWhenAccountRegist(
          globalEmailAddress,
          globalReservationInput?.kanjiLast,
          globalReservationInput?.kanjiFirst,
          Mail_TYPES.RESEND.string,
          ""
        );
      }
      // マイページ-パスワード変更用（ログイン後）
      if (location.pathname?.includes("mypage/password-setting")) {
        data = await sendCertificationNumberMailWhenPasswordReset(
          globalAccessToken,
          globalEmailAddress
        );
      }
      // パスワードをお忘れの方はこちらから用（ログイン前）
      if (location.pathname?.includes("reset-password")) {
        data = await sendCertificationNumberMailWhenPasswordResetBeforeLogin(
          globalEmailAddress
        );
      }
      // マイページ-メールアドレス変更用
      if (location.pathname?.includes("mypage/mail-setting")) {
        data = await sendCertificationNumberMailWhenEmailUpdate(
          globalAccessToken,
          globalEmailAddress,
          globalLoginUserInfo?.userInfo.kanjiLast,
          globalLoginUserInfo?.userInfo.kanjiFirst
        );
      }
      if (data.result.status === "OK") {
        // 同じ画面にとどまる
        showToast("メールアドレス宛に認証番号を再送信しました", "success");
      } else {
        setErrorMessage(data?.result?.error_message);
      }
    } catch (error) {
      showToast("メールの送信に失敗しました", "error");
    }
  };

  return (
    <div className="certification-number-input">
      <div className="main-area">
        <div className="title">メールをご確認ください</div>
        <div className="detail">
          以下のメールアドレス宛に認証番号を送信しました。
          <br />
          ご確認いただき、認証番号を入力してください。
          <br />
          有効期限は60分です。
          <br />
          ※お送りしたメールが迷惑メールフォルダ内にある場合がございますので、ご確認ください。
        </div>
        <div className="form">
          <Form
            form={form}
            name="certification"
            layout="vertical"
            initialValues={{ remember: true }}
          >
            <Form.Item
              className="ant-label"
              label="登録メールアドレス"
              name="email"
            >
              <label className="email-label">
                <div className="email-area">{globalEmailAddress}</div>
              </label>
            </Form.Item>
            <Row gutter={16} className="certification-row">
              <Col span={24}>
                <Form.Item
                  className="ant-label"
                  label="認証番号"
                  name="certification"
                  rules={[
                    {
                      required: true,
                      message: "6桁の番号を入力してください",
                    },
                    { validator: validateCertificationNumber },
                  ]}
                  validateStatus={errors.certification ? "error" : ""}
                  help={
                    errors.certification ? (
                      <span className="ant-validation-message">
                        6桁の番号を入力してください
                      </span>
                    ) : (
                      ""
                    )
                  }
                >
                  <Input
                    className="ant-input"
                    ref={certificationInput}
                    suffix={
                      errors.certification ? (
                        <ExclamationCircleOutlined />
                      ) : null
                    }
                    onChange={() => onValueChange("certification")}
                  />
                </Form.Item>
              </Col>
            </Row>
            {errorMessage && (
              <div className="error-area">
                <div className="icon-area">
                  <WarningFilled className="icon" />
                </div>
                <label>{errorMessage}</label>
              </div>
            )}
          </Form>
        </div>
        <div className="general-buttons">
          <GeneralButton
            buttonLabel="認証"
            type={
              Object.values(errors).some((error) => error) ||
              !certificationNumberCheck
                ? "disabled"
                : "primary-blue"
            }
            onClick={() => handleVerificationClick()}
          />
          <GeneralButton
            buttonLabel={"設定メールの再送信"}
            type={"link-blue"}
            onClick={() => handleResendMail()}
          />
        </div>
      </div>
    </div>
  );
}

export default CertificationNumberInputPage;
